import { useState } from 'react';
import {
  intelAnalyst,
  intelCmsFacilities,
  intelCmsLookup,
  intelCmsScore,
  intelDcf,
  intelMonteCarlo,
  intelSemantic,
  intelSummaryPdfPath,
  intelWatch,
  type MonteCarloResult,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { exportCsv } from '../../ui/exportCsv';
import { EmptyState, Skeleton } from '../../ui/states';
import { A, CAP, CAP9, CARD, DASH, MOD_TAG, bars, onSet, plusK, Tile, useBreakdown, type TabProps } from './common';
import { GC, G, M, MINUS, PPD, bandOfBeds, gradeOf, verdictOf, waccText, type Deal } from './model';
import { peerOf, staffingLine, usePeerStaffing } from './staffing';
import { BuildLabel } from '../../ui/buildLabels';

/** A percentage the V4 way: no "-0%", and the design's minus sign (aiVals pc). */
export const pc = (x: number, d?: number) => {
  const t = (x * 100).toFixed(d === undefined ? 0 : d);
  return (/^-0(\.0+)?$/.test(t) ? t.slice(1) : t.replace('-', MINUS)) + '%';
};

/** The deal sheet's sub-tabs. V4: "Benchmarks ↗" opens the Benchmarks tab instead of a panel. */
const EVALS: [string, string][] = [
  ['val', 'Valuation'],
  ['fin', 'Financials'],
  ['ops', 'Operations'],
  ['bench', 'Benchmarks ↗'],
  ['reimb', 'Reimbursement'],
  ['upside', 'Upside'],
  ['risk', 'Risk'],
];

const OPEX_FIX: [string, number][] = [
  ['Supplies / pharmacy', 0.07],
  ['Dietary', 0.045],
  ['Facilities', 0.04],
  ['Insurance / admin', 0.07],
  ['Bad debt', 0.015],
];

const BTN_SOFT = 'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:14px;text-decoration:none;background:#FBF8F1;color:#3C454F;border:1px solid rgba(34,39,46,.16);white-space:nowrap;';
const BTN_RUN = 'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:14px;font-weight:600;text-decoration:none;background:rgba(47,138,111,.12);color:#1F6B52;border:1px solid rgba(47,138,111,.34);white-space:nowrap;';
const BTN_SMALL = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;text-decoration:none;background:#FBF8F1;color:#3C454F;border:1px solid rgba(34,39,46,.16);white-space:nowrap;';
const TILE_V = 'font-size:19px;font-weight:700;letter-spacing:-.02em;';
const MODE = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 15px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:rgba(79,138,214,.15);color:#2F5C97;font-weight:600;border:1px solid rgba(47,92,151,.34);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);');

/**
 * Analysis (V4 aiIsTarget, T16448; aiVals L31600): the deal-book list and the full deal sheet
 * of the facility on the right, read from the one deal card (model.ts).
 */
export function AnalysisTab(p: TabProps) {
  const { st, set, deals, f } = p;
  const filt = st.aiFilter || 'All';
  const sort = st.aiSort || 'Grade';
  const q = st.aiQuery || '';
  // Semantic search: the catalog facilities the embedding search matched; the list keeps the deal-book ones.
  const [sem, setSem] = useState<{ q: string; ids: Set<string>; note: string | null } | null>(null);
  const semOn = !!sem && sem.q === q && !sem.note;
  const ql = q.toLowerCase();
  const runSemantic = async () => {
    if (!q.trim()) return setSem(null);
    try {
      const r = await intelSemantic(p.client, { body: { q, limit: 50 } });
      if (!r.available) return setSem({ q, ids: new Set(), note: r.message ?? 'Semantic search is not switched on for this catalog.' });
      setSem({ q, ids: new Set(r.results.flatMap((x) => [String(x.id), String(x.id).split(':').pop() ?? ''])), note: null });
    } catch (e) {
      setSem({ q, ids: new Set(), note: (e as Error).message });
    }
  };
  const shown = deals
    .filter((x) => filt === 'All' || x.status === filt)
    .filter((x) => (semOn ? !!x.providerId && sem!.ids.has(x.providerId) : !q || (x.name + ' ' + (x.city || '') + ' ' + (x.stt || '')).toLowerCase().indexOf(ql) >= 0))
    .slice()
    .sort((a, b) => (sort === 'Grade' ? b.comp - a.comp : sort === 'Rating' ? b.stars - a.stars : sort === 'Occupancy' ? b.occ - a.occ : sort === 'Margin' ? b.margin - a.margin : b.beds - a.beds));

  return (
    <div style={sx('display:flex;align-items:flex-start;gap:16px;min-width:0;flex-wrap:wrap;row-gap:14px;')}>
      <div style={sx('flex:0 1 286px;width:286px;min-width:240px;display:flex;flex-direction:column;gap:9px;')}>
        <input
          type="text"
          value={q}
          onChange={(e) => set({ aiQuery: e.target.value })}
          onKeyDown={(e) => {
            // Enter asks the catalog's semantic search (POST /facilities/search-semantic).
            if (e.key === 'Enter') void runSemantic();
          }}
          placeholder={'Search facilities by name or city…'}
          aria-label="Search the deal book"
          title="Type to filter; press Enter for a semantic search over the catalog"
          style={sx('box-sizing:border-box;width:100%;height:34px;padding:0 12px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FCFAF4;font:inherit;font-size:16px;color:#22272E;')}
        />
        {sem?.note && sem.q === q && (
          <div role="status" style={sx('font-size:14px;color:#54606C;')}>
            {sem.note}
          </div>
        )}
        <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;')}>
          {['All', 'Acquired', 'Pipeline'].map((c) => (
            <A
              key={c}
              onClick={onSet(set, { aiFilter: c })}
              style={
                'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' +
                (filt === c ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);')
              }
            >
              {c}
            </A>
          ))}
        </div>
        <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;')}>
          <span style={sx(CAP)}>Sort</span>
          {['Grade', 'Rating', 'Occupancy', 'Margin', 'Beds'].map((s) => (
            <A
              key={s}
              onClick={onSet(set, { aiSort: s })}
              style={
                'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;text-decoration:none;white-space:nowrap;' +
                (sort === s ? 'background:rgba(79,138,214,.15);color:#2F5C97;font-weight:600;' : 'background:transparent;color:#54606C;border:1px solid rgba(34,39,46,.14);')
              }
            >
              {s}
            </A>
          ))}
        </div>
        <CatalogCount p={p} n={shown.length} />
        <div style={sx('display:flex;flex-direction:column;gap:3px;')}>
          {shown.map((x) => {
            const i = deals.indexOf(x);
            return (
              <A
                key={x.id}
                hover="intel-h-list"
                onClick={onSet(set, { aiSel: i })}
                style={
                  'animation:riseIn .28s cubic-bezier(.22,.61,.36,1);animation-delay:' +
                  Math.min(300, i * 26) +
                  'ms;display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:9px 11px;border-radius:9px;text-decoration:none;color:#22272E;' +
                  (i === p.sel ? 'background:rgba(79,138,214,.13);border:1px solid rgba(47,92,151,.3);' : 'background:transparent;border:1px solid transparent;')
                }
              >
                <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;flex:0 0 22px;border-radius:6px;font-size:14px;font-weight:700;color:#fff;background:' + GC[x.grade] + ';')}>{x.grade}</span>
                <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:600;overflow-wrap:break-word;')}>{x.name}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;overflow-wrap:break-word;')}>
                    {x.city + ', ' + x.stt + ' · ' + x.beds + ' beds · ' + x.status.toLowerCase()}
                  </span>
                </span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>{x.mult.toFixed(1) + 'x'}</span>
              </A>
            );
          })}
        </div>
      </div>

      <div style={sx('flex:1 1 380px;min-width:0;display:flex;flex-direction:column;gap:13px;')}>
        {f ? <DealSheet p={p} f={f} /> : <EmptyState title="No facility in the deal book yet" body="Track a facility from the catalog and its deal sheet opens here." />}
      </div>
    </div>
  );
}

/** "276,000+ in catalog · showing 6": the catalog total is the served breakdown. */
function CatalogCount({ p, n }: { p: TabProps; n: number }) {
  const bd = useBreakdown(p.client);
  const total = bd.data ? plusK(bd.data.total) : DASH;
  return <div style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{total + ' in catalog · showing ' + n}</div>;
}

function DealSheet({ p, f }: { p: TabProps; f: Deal }) {
  const { st, set, client, readOnly } = p;
  const evalTab = st.aiEval || 'val';
  const plan = st.aiMode === 'Plan';
  const [msg, setMsg] = useState<string | null>(null);

  // Plan levers
  const occLift = st.aiOccLift ?? 3;
  const mixShift = st.aiMixShift ?? 5;
  const agCut = st.aiAgencyCut ?? 50;
  const occRev = ((occLift / 100) * f.beds * 365 * f.ppd) / 1e6;
  const occLabor = occRev * 0.35;
  const mixGain = ((mixShift / 100) * (PPD.private - PPD.medicaid) * f.pd) / 1e6;
  const agGain = (agCut / 100) * f.agency * f.rev;
  const planEbitda = f.ebitda + occRev - occLabor + mixGain + agGain;
  const planEV = planEbitda * f.mult;
  const planIrr = f.ret(planEbitda, f.mult).irr;

  const n = (k: keyof typeof st) => (e: React.ChangeEvent<HTMLInputElement>) => set({ [k]: parseFloat(e.target.value) } as never);

  const track = async () => {
    if (!f.providerId) return;
    try {
      await intelWatch(client, { body: { facilityIds: [f.providerId] } });
      setMsg('Tracking ' + f.name + '. Sealed to the Truth Ledger.');
    } catch (e) {
      setMsg((e as Error).message);
    }
  };
  const exportSummary = () =>
    exportCsv(
      ['Facility', 'City', 'State', 'Beds', 'Occupancy', 'CMS stars', 'EBITDA ($M)', 'Entry EV ($M)', 'DCF EV ($M)', 'IRR', 'MOIC', 'Grade (model)'],
      [[f.name, f.city, f.stt, f.beds, f.occ, f.stars, f.ebitda, f.entry, f.ev, f.irr, f.moic, f.grade]],
      f.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '-summary.csv',
    );
  const memo = async () => {
    if (!f.providerId) return;
    try {
      const res = await client.raw(intelSummaryPdfPath(f.providerId));
      if (!res.ok) throw new Error(`The IC memo could not be made (${res.status}).`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = f.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '-ic-memo.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setMsg((e as Error).message);
    }
  };

  return (
    <>
      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:flex-start;gap:13px;flex-wrap:wrap;')}>
          <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:42px;height:42px;flex:0 0 42px;border-radius:10px;font-size:16px;font-weight:700;color:#fff;background:' + GC[f.grade] + ';')}>
            {f.name.split(' ').slice(0, 2).map((w) => w[0]).join('')}
          </span>
          <div style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;')}>
              <span style={sx('font-size:19px;font-weight:700;letter-spacing:-.015em;')}>{f.name}</span>
              <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:6px;font-size:14px;font-weight:700;color:#fff;background:' + GC[f.grade] + ';')}>{f.grade}</span>
              <BuildLabel><span style={sx('flex:0 0 auto;white-space:nowrap;' + MOD_TAG)}>MODELED EST.</span></BuildLabel>
            </div>
            <div style={sx('font-size:16px;color:#54606C;margin-top:4px;')}>
              <span>{f.city + ', ' + f.stt + ' · ' + f.beds + ' beds · Occ ' + pc(f.occ) + ' · ' + f.stars + '★ CMS · EBITDA ' + M(f.ebitda)}</span> &middot; <span style={sx('color:#1F6B52;font-weight:600;')}>{f.status}</span>
            </div>
          </div>
          <div style={sx('text-align:right;')}>
            <div style={sx('font-size:24px;font-weight:700;letter-spacing:-.02em;')}>{M(f.entry)}</div>
            <div style={sx(CAP)}>{'Price · ' + f.mult.toFixed(1) + '× EBITDA'}</div>
            <div
              style={sx('font-size:14px;color:#54606C;margin-top:3px;')}
              title={'Long-run cash-flow (DCF) value at ' + waccText(f) + ', g 2.5%. ' + (f.ev > f.entry * 1.5 ? 'It is ' + (f.ev / f.entry).toFixed(1) + '× the price: check the assumptions before relying on it.' : 'Close to the price.')}
            >
              {'Cash-flow check '}
              <span>{M(f.ev)}</span>
            </div>
          </div>
        </div>
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:12px;')}>
          {!readOnly && (
            <A style={BTN_SOFT} onClick={track}>
              + Track
            </A>
          )}
          <A style={BTN_SOFT} onClick={exportSummary}>
            Export summary
          </A>
          <A style="display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:14px;font-weight:600;text-decoration:none;background:rgba(79,138,214,.15);color:#2F5C97;border:1px solid rgba(47,92,151,.34);white-space:nowrap;" onClick={memo}>
            Download IC Memo (PDF)
          </A>
        </div>
        {msg && (
          <div role="status" style={sx('font-size:14px;color:#54606C;margin-top:8px;')}>
            {msg}
          </div>
        )}
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin-top:13px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
          <span
            style={sx(
              'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;letter-spacing:.08em;white-space:nowrap;' +
                (f.irr >= 0.12 ? 'color:#1F6B52;background:rgba(47,138,111,.14);' : f.irr >= 0.08 ? 'color:#264C7E;background:rgba(47,92,151,.12);' : 'color:#5B6B7C;background:rgba(91,107,124,.14);'),
            )}
          >
            {verdictOf(f.irr)}
          </span>
          <span style={sx('font-size:16px;color:#54606C;')}>
            {f.irr >= 0.12
              ? 'Clears the 12% hurdle at ' + M(f.entry) + ' · ' + pc(f.irr, 1) + ' IRR, ' + f.moic.toFixed(2) + '×'
              : f.irr >= 0.08
                ? 'Below the 12% hurdle at ' + M(f.entry) + ': pursue only at a lower price'
                : 'Below 8% at ' + M(f.entry) + ': pass at this price'}
          </span>
        </div>
      </div>

      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:13px 17px;display:flex;align-items:center;gap:16px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx(CAP)}>Peer benchmarks</span>
        <span style={sx('font-size:20px;font-weight:700;letter-spacing:-.02em;')}>
          {f.comp.toFixed(0)}
          <span style={sx('font-size:14px;color:#54606C;font-weight:500;')}>/100</span>
        </span>
        <span style={sx('font-size:14px;color:#54606C;')}>{f.stt + ' · ' + bandOfBeds(f.beds) + ' beds · n=' + (f.peerN ?? DASH) + ' · ' + f.type}</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>Scored by Aithora from CMS and cost-report data</span>
      </div>

      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:11px;')}>
        <Tile label="Entry price" val={M(f.entry)} note={f.mult.toFixed(1) + 'x EBITDA'} pad="9px 2px" valStyle={TILE_V} noteStyle="font-size:14px;color:#54606C;" />
        <Tile label="DCF value" val={M(f.ev)} note={waccText(f) + ' · g 2.5%'} pad="9px 2px" valStyle={TILE_V} noteStyle="font-size:14px;color:#54606C;" />
        <Tile
          label="Return"
          val={(Math.abs(f.irr - 0.12) < 0.0005 ? pc(f.irr, 2) : pc(f.irr, 1)) + ' IRR'}
          note={'MOIC ' + f.moic.toFixed(1) + 'x · 5-yr' + (f.irr < 0.1195 ? ' · under the 12% hurdle' : f.irr < 0.12 ? ' · at the 12% hurdle' : '')}
          pad="9px 2px"
          valStyle={TILE_V + 'color:' + (f.ev >= f.entry ? (f.irr >= 0.1195 ? '#1F6B52' : '#22272E') : '#B3261E') + ';'}
          noteStyle="font-size:14px;color:#54606C;"
        />
        <Tile label="Spread to DCF" val={(f.ev >= f.entry ? '+' : MINUS) + M(Math.abs(f.ev - f.entry))} note={f.ev >= f.entry ? 'accretive' : 'rich'} pad="9px 2px" valStyle={TILE_V + 'color:#1F6B52;'} noteStyle="font-size:14px;color:#54606C;" />
      </div>
      <div style={sx('font-size:14px;color:#54606C;margin-top:-6px;')}>Entry price, DCF value, return and spread are modeled estimates. Only the grade is scored from source data.</div>

      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
        <A onClick={onSet(set, { aiMode: 'Base' })} style={MODE(!plan)}>
          Base
        </A>
        <A onClick={onSet(set, { aiMode: 'Plan' })} style={MODE(plan)}>
          Plan
        </A>
        <span style={sx('font-size:16px;color:#54606C;')}>{plan ? 'Plan underwriting. Levers below are modeled, not committed.' : 'As-is underwriting. Switch to Plan to model the value-creation levers.'}</span>
      </div>

      {plan && (
        <div style={sx('display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,1fr);gap:13px;')}>
          <div style={sx(CARD)}>
            <div style={sx(CAP + 'margin-bottom:12px;')}>Value-creation levers</div>
            <div style={sx('display:flex;flex-direction:column;gap:14px;')}>
              <Slider label="Occupancy lift" val={'+' + occLift + ' pts'} note={Math.round((occLift / 100) * f.beds) + ' beds, ~' + ((occLift / 100) * f.beds * 0.35).toFixed(1) + ' FTEs'} min={0} max={8} step={1} value={occLift} onChange={n('aiOccLift')} />
              <Slider label={'Payer mix → private'} val={'+' + mixShift + ' pts'} note={'+' + M(mixGain) + ' revenue'} min={0} max={10} step={1} value={mixShift} onChange={n('aiMixShift')} />
              <Slider label="Agency cut" val={agCut + '%'} note={MINUS + M(agGain) + ' cost'} min={0} max={100} step={5} value={agCut} onChange={n('aiAgencyCut')} />
            </div>
          </div>
          <div style={sx(CARD)}>
            <div style={sx(CAP + 'margin-bottom:12px;')}>Value bridge</div>
            <div style={sx('display:flex;flex-direction:column;gap:10px;')}>
              {(
                [
                  ['Occupancy', occRev, '#1F6B52'],
                  ['Payer mix', mixGain, '#1F6B52'],
                  ['Agency cut', agGain, '#1F6B52'],
                  ['New labor', -occLabor, '#B3261E'],
                ] as [string, number, string][]
              ).map(([label, v, c]) => (
                <div key={label}>
                  <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;font-size:14px;margin-bottom:4px;')}>
                    <span style={sx('color:#3C454F;')}>{label}</span>
                    <span style={sx('flex:1;')} />
                    <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;')}>{(v >= 0 ? '+' : MINUS) + M(Math.abs(v))}</span>
                  </div>
                  <div style={sx('height:6px;border-radius:3px;background:rgba(34,39,46,.07);')}>
                    <div style={sx(bars(Math.abs(v), Math.max(occRev, mixGain, agGain, occLabor), c))} />
                  </div>
                </div>
              ))}
            </div>
            <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:13px;padding-top:11px;border-top:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('font-size:16px;font-weight:600;')}>Plan EBITDA</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:17px;font-weight:700;')}>{M(planEbitda)}</span>
            </div>
            <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
              <span style={sx('font-size:16px;font-weight:600;')}>Plan EV</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:17px;font-weight:700;color:#1F6B52;')}>{M(planEV)}</span>
            </div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:6px;')}>{'EBITDA +' + M(planEbitda - f.ebitda) + ' · EV +' + M(planEV - f.entry) + ' · IRR ' + pc(planIrr)}</div>
          </div>
        </div>
      )}

      <div role="tablist" aria-label="Deal sheet" style={sx('display:flex;align-items:center;flex-wrap:wrap;row-gap:6px;border-bottom:1px solid rgba(34,39,46,.12);')}>
        {EVALS.map(([k, label]) => (
          <A
            key={k}
            onClick={(e) => {
              e.preventDefault();
              if (k === 'bench') p.go('bench');
              else set({ aiEval: k });
            }}
            style={
              'display:inline-flex;align-items:center;height:32px;padding:0 4px;margin-right:18px;font-size:16px;text-decoration:none;border-bottom:2px solid ' +
              (evalTab === k ? '#2F5C97;color:#2F5C97;font-weight:600;' : 'transparent;color:#54606C;font-weight:500;')
            }
          >
            {label}
          </A>
        ))}
      </div>

      {evalTab === 'val' && <Valuation p={p} f={f} />}
      {evalTab === 'fin' && <Financials f={f} />}
      {evalTab === 'ops' && <Operations p={p} f={f} occLift={occLift} occRev={occRev} occLabor={occLabor} mixGain={mixGain} agGain={agGain} planEbitda={planEbitda} />}
      {evalTab === 'reimb' && <Reimbursement f={f} />}
      {evalTab === 'upside' && <Upside f={f} />}
      {evalTab === 'risk' && <Risk p={p} f={f} />}

      <LiveRun p={p} f={f} />
    </>
  );
}

function Slider({ label, val, note, min, max, step, value, onChange, color = '#1F6B52', gap = '5px' }: { label: string; val: string; note?: string; min: number; max: number; step: number; value: number; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; color?: string; gap?: string }) {
  return (
    <div>
      <div style={sx(`display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:${gap};`)}>
        <span style={sx('font-size:16px;font-weight:600;')}>{label}</span>
        <span style={sx('flex:1;')} />
        <span style={sx(`font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:${color};`)}>{val}</span>
        {note !== undefined && <span style={sx('font-size:14px;color:#54606C;')}>{note}</span>}
      </div>
      <input type="range" aria-label={label} min={min} max={max} step={step} value={value} onChange={onChange} style={sx(`margin:0;width:100%;accent-color:${color};`)} />
    </div>
  );
}

function Valuation({ p, f }: { p: TabProps; f: Deal }) {
  const mc = useQuery<MonteCarloResult>(
    (signal) =>
      intelMonteCarlo(p.client, {
        body: { baseRevenue: f.rev * 1e6, target: 'ev', ev: f.ev * 1e6, askEv: f.entry * 1e6, simulations: 1600, bins: 23, years: 5 },
        signal,
      }),
    [p.client, f.id],
  );
  const scoreRows: [string, string, number, number][] = [
    ['Occupancy', pc(f.occ), 25, Math.min(1, Math.max(0, (f.occ - 0.55) / 0.43))],
    ['Payer mix', pc(f.priv) + ' private', 20, Math.min(1, f.priv / 0.45)],
    ['CMS star rating', f.stars + '★', 20, f.stars / 5],
    ['Staffing (HPRD)', f.hprd.toFixed(2), 15, Math.min(1, f.hprd / 4)],
    ['Quality measures', f.qual + '/100', 20, f.qual / 100],
  ];
  return (
    <>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:13px;')}>
        <div style={sx(CARD)}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx(CAP)}>5-year DCF</span>
            <BuildLabel><span style={sx(MOD_TAG)}>MODELED</span></BuildLabel>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>{waccText(f)} &middot; g 2.5%</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr);gap:6px 10px;' + CAP9 + 'padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.1);')}>
            <span>Year</span>
            <span style={sx('text-align:right;')}>EBITDA</span>
            <span style={sx('text-align:right;')}>FCF</span>
            <span style={sx('text-align:right;')}>PV</span>
          </div>
          {f.rows.map((d) => (
            <div key={d.year} style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr);gap:6px 10px;font-size:16px;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.06);font-variant-numeric:tabular-nums;')}>
              <span style={sx('color:#3C454F;')}>{d.year}</span>
              <span style={sx('text-align:right;')}>{d.ebitda}</span>
              <span style={sx('text-align:right;')}>{d.fcf}</span>
              <span style={sx('text-align:right;')}>{d.pv}</span>
            </div>
          ))}
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;padding-top:10px;')}>
            <span style={sx('font-size:16px;font-weight:600;')}>Terminal PV</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:600;')}>{M(f.tvpv)}</span>
          </div>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:6px;padding-top:9px;border-top:1px solid rgba(34,39,46,.1);')}>
            <span style={sx('font-size:16px;font-weight:700;')}>Enterprise value</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:17px;font-weight:700;')}>{M(f.ev)}</span>
          </div>
        </div>

        <div style={sx(CARD)}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx(CAP)}>Monte Carlo</span>
            <BuildLabel><span style={sx(MOD_TAG)}>MODELED</span></BuildLabel>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>{mc.data ? mc.data.simulations.toLocaleString('en-US') + ' sims' : DASH}</span>
          </div>
          <MonteCarlo mc={mc.data} loading={mc.loading && !mc.data} f={f} />
        </div>
      </div>

      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
          <span style={sx(CAP)}>Scenario score &middot; model (what-if)</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>illustrative weighting - not the deal grade</span>
        </div>
        <div style={sx('display:flex;align-items:flex-start;gap:22px;flex-wrap:wrap;row-gap:6px;')}>
          <div style={sx('flex:1;min-width:280px;display:flex;flex-direction:column;gap:9px;')}>
            {scoreRows.map(([label, val, w, x]) => (
              <div key={label} style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;')}>
                <span style={sx('width:118px;flex:0 0 118px;font-size:14px;color:#3C454F;')}>{label}</span>
                <span style={sx('flex:1;height:6px;border-radius:3px;background:rgba(34,39,46,.07);')}>
                  <span style={sx('display:block;' + bars(x, 1, x >= 0.72 ? '#1F6B52' : x >= 0.5 ? '#2F5C97' : '#5B6B7C'))} />
                </span>
                <span style={sx('width:74px;flex:0 0 74px;text-align:right;font-variant-numeric:tabular-nums;font-size:14px;')}>{val}</span>
                <span style={sx('width:34px;flex:0 0 34px;text-align:right;font-size:14px;color:#54606C;')}>{w + '%'}</span>
              </div>
            ))}
          </div>
          <div style={sx('text-align:right;')}>
            <div style={sx(CAP)}>Model</div>
            <div style={sx('font-size:30px;font-weight:700;letter-spacing:-.02em;')}>
              {f.comp.toFixed(0)}
              <span style={sx('font-size:16px;color:#54606C;font-weight:500;')}>/100</span>
            </div>
            <div style={sx('font-size:14px;color:#54606C;')}>{f.mult.toFixed(1) + 'x modeled'}</div>
          </div>
        </div>
      </div>
    </>
  );
}

/** The EV distribution from POST /monte-carlo (EV mode). */
function MonteCarlo({ mc, loading, f }: { mc: MonteCarloResult | null; loading: boolean; f: Deal }) {
  if (loading) return <Skeleton rows={4} label="Running the simulation" />;
  const bins = mc?.bins ?? null;
  if (!mc || !bins || !bins.length) {
    return <EmptyState compact title="No valuation distribution yet" body="The Monte Carlo run has not returned an EV distribution for this facility." />;
  }
  const max = Math.max(...bins.map((b) => b.count)) || 1;
  const P = mc.percentiles;
  const toM = (x: number | null | undefined) => (x == null ? DASH : M(x / 1e6));
  const entryAbs = f.entry * 1e6;
  return (
    <>
      <div style={sx('display:flex;align-items:flex-end;gap:3px;height:104px;')}>
        {bins.map((b, i) => (
          <div key={i} style={sx('flex:1;border-radius:2px 2px 0 0;height:' + Math.max(3, Math.round((96 * b.count) / max)) + 'px;background:' + (b.x < entryAbs ? '#B3261E' : '#2F5C97') + ';')} />
        ))}
      </div>
      <div style={sx('display:flex;align-items:center;font-size:14px;color:#54606C;margin-top:6px;')}>
        <span>{toM(mc.low)}</span>
        <span style={sx('flex:1;text-align:center;')}>{'asking ' + M(f.entry)}</span>
        <span>{toM(mc.high)}</span>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:10px;margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
        <div>
          <div style={sx(CAP9)}>P10</div>
          <div style={sx('font-size:16px;font-weight:700;color:#B3261E;')}>{toM(P.p10)}</div>
        </div>
        <div>
          <div style={sx(CAP9)}>Typical</div>
          <div style={sx('font-size:16px;font-weight:700;')}>{toM(P.p50)}</div>
        </div>
        <div>
          <div style={sx(CAP9)}>P90</div>
          <div style={sx('font-size:16px;font-weight:700;color:#1F6B52;')}>{toM(P.p90)}</div>
        </div>
        <div>
          <div style={sx(CAP9)}>{'P(EV≥ask)'}</div>
          <div style={sx('font-size:16px;font-weight:700;color:#1F6B52;')}>{mc.pAboveAsk == null ? DASH : pc(mc.pAboveAsk)}</div>
        </div>
      </div>
    </>
  );
}

function Financials({ f }: { f: Deal }) {
  const laborEmp = 1 - f.margin - (f.agency + OPEX_FIX.reduce((s, r) => s + r[1], 0));
  const opex: [string, number][] = [['Labor · employed', laborEmp], ['Labor · agency', f.agency], ...OPEX_FIX];
  const ret: [string, string][] = [
    ['Entry EV', M(f.entry)],
    ['Debt @ 60%', M(f.debt)],
    ['Equity in', M(f.eqIn)],
    ['Exit EBITDA · yr 5', M(f.e5)],
    ['Exit EV @ ' + f.mult.toFixed(1) + 'x', M(f.exitEV)],
    ['IRR', pc(f.irr)],
    ['MOIC', f.moic.toFixed(2) + 'x'],
  ];
  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:13px;')}>
      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
          <span style={sx(CAP)}>OpEx breakdown</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>{M(f.rev) + ' revenue · ' + pc(f.margin, 1) + ' EBITDA margin'}</span>
        </div>
        <div style={sx('display:flex;flex-direction:column;gap:10px;')}>
          {opex.map(([label, share]) => (
            <div key={label}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;font-size:14px;margin-bottom:4px;')}>
                <span style={sx('color:#3C454F;')}>{label}</span>
                <span style={sx('flex:1;')} />
                <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;')}>{M(share * f.rev)}</span>
                <span style={sx('width:48px;text-align:right;color:#54606C;')}>{pc(share, 1)}</span>
              </div>
              <div style={sx('height:6px;border-radius:3px;background:rgba(34,39,46,.07);')}>
                <div style={sx(bars(share, 0.55, label.indexOf('agency') > 0 ? '#5B6B7C' : '#2F5C97'))} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
          <span style={sx(CAP)}>Returns</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>5-yr hold &middot; 60% leverage</span>
        </div>
        {ret.map(([label, val]) => (
          <div key={label} style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
            <span style={sx('color:#3C454F;')}>{label}</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;')}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Operations({ p, f, occLift, occRev, occLabor, mixGain, agGain, planEbitda }: { p: TabProps; f: Deal; occLift: number; occRev: number; occLabor: number; mixGain: number; agGain: number; planEbitda: number }) {
  const laborEmp = 1 - f.margin - (f.agency + OPEX_FIX.reduce((s, r) => s + r[1], 0));
  // No federal staffing minimum (rescinded 2026-02-02): HPRD reads against same-type, same-state peers.
  const peer = peerOf(usePeerStaffing(p.client, [{ type: f.type, state: f.stt }]), f.type, f.stt);
  const med = peer?.median ?? null;
  const ops: [string, string, string, boolean][] = [
    ['Total nurse HPRD', f.hprd.toFixed(2), med == null ? 'typical for peers ' + DASH : 'typical for peers ' + med.toFixed(2), med == null || f.hprd >= med],
    ['Agency share of labor', pc(f.agency, 1), 'target 7%', f.agency <= 0.07],
    ['Labor % of revenue', pc(laborEmp + f.agency, 1), 'band 58–62%', laborEmp + f.agency <= 0.62],
    ['Occupancy', pc(f.occ), 'target 82%', f.occ >= 0.82],
    ['Revenue / patient-day', '$' + f.ppd.toFixed(0), 'blended', true],
    ['EBITDA margin', pc(f.margin, 1), 'target 18%', f.margin >= 0.18],
  ];
  const vcp: [string, number, string][] = [
    ['Convert agency to employed', agGain, 'Labor'],
    ['Census +' + occLift + ' pts', occRev - occLabor, 'Census'],
    ['Payer mix → private', mixGain, 'Revenue'],
    ['Procurement + dietary', f.rev * 0.006, 'OpEx'],
  ];
  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:13px;')}>
      <div style={sx(CARD)}>
        <div style={sx(CAP + 'margin-bottom:12px;')}>Labor &amp; efficiency</div>
        {ops.map(([label, val, target, ok]) => (
          <div key={label} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
            <span style={sx('color:#3C454F;')}>{label}</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>{target}</span>
            <span style={sx('font-variant-numeric:tabular-nums;font-weight:700;color:' + (ok ? '#1F6B52' : '#5B6B7C') + ';')}>{val}</span>
          </div>
        ))}
      </div>
      <div style={sx(CARD)}>
        <div style={sx(CAP + 'margin-bottom:12px;')}>Value-creation plan</div>
        {vcp.map(([label, v, tag]) => (
          <div key={label} style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
            <span style={sx('color:#3C454F;')}>{label}</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#54606C;background:rgba(34,39,46,.07);border-radius:4px;padding:2px 6px;font-weight:600;')}>{tag}</span>
            <span style={sx('font-variant-numeric:tabular-nums;font-weight:700;color:#1F6B52;')}>{'+' + M(v)}</span>
          </div>
        ))}
        <div style={sx('font-size:14px;color:#54606C;margin-top:11px;')}>{M(planEbitda + f.rev * 0.006) + ' pro-forma EBITDA · +' + M((planEbitda - f.ebitda + f.rev * 0.006) * f.mult) + ' value uplift'}</div>
      </div>
    </div>
  );
}

function Reimbursement({ f }: { f: Deal }) {
  const upMix = (0.05 * (PPD.private - PPD.medicaid) * f.pd) / 1e6;
  const rows: [string, number, number][] = [
    ['Medicare', f.mcare, PPD.medicare],
    ['Medicaid', f.mcaid, PPD.medicaid],
    ['Private', f.priv, PPD.private],
  ];
  const cols = 'display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) 1.2fr 1fr;gap:6px 10px;';
  return (
    <div style={sx(CARD)}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
        <span style={sx(CAP)}>Payer mix</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>{'$' + f.ppd.toFixed(0) + ' blended · ' + M(f.rev) + ' annual'}</span>
      </div>
      <div style={sx(cols + CAP9 + 'padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.1);')}>
        <span>Payer</span>
        <span style={sx('text-align:right;')}>Mix</span>
        <span style={sx('text-align:right;')}>$/day</span>
        <span style={sx('text-align:right;')}>Annual</span>
        <span style={sx('text-align:right;')}>% rev</span>
      </div>
      {rows.map(([payer, mix, ppd]) => (
        <div key={payer} style={sx(cols + 'font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
          <span style={sx('font-weight:600;')}>{payer}</span>
          <span style={sx('text-align:right;font-variant-numeric:tabular-nums;')}>{pc(mix)}</span>
          <span style={sx('text-align:right;font-variant-numeric:tabular-nums;')}>{'$' + ppd}</span>
          <span style={sx('text-align:right;font-variant-numeric:tabular-nums;')}>{M((mix * ppd * f.pd) / 1e6)}</span>
          <span style={sx('text-align:right;font-variant-numeric:tabular-nums;color:#54606C;')}>{pc((mix * ppd) / f.ppd, 1)}</span>
        </div>
      ))}
      <div style={sx('font-size:16px;color:#1F6B52;font-weight:600;margin-top:12px;')}>{'Shift 5 pts Medicaid → private: +$' + (0.05 * 92).toFixed(2) + '/day · +' + M(upMix) + '/yr'}</div>
    </div>
  );
}

function Upside({ f }: { f: Deal }) {
  const upOcc = (Math.max(0, 0.95 - f.occ) * f.beds * 365 * f.ppd * 0.65) / 1e6;
  const upMix = (0.05 * (PPD.private - PPD.medicaid) * f.pd) / 1e6;
  const upAg = Math.max(0, f.agency - 0.03) * f.rev;
  const upTotal = (upOcc + upMix + upAg) * f.mult;
  const rows: [string, number, boolean][] = [
    ['As-is DCF enterprise value', f.ev, false],
    ['Occupancy to 95%', upOcc * f.mult, true],
    ['Payer mix +5 pts private', upMix * f.mult, true],
    ['Agency to 3%', upAg * f.mult, true],
  ];
  return (
    <div style={sx(CARD)}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
        <span style={sx(CAP)}>Full-potential walk</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>each opportunity valued at the exit multiple</span>
      </div>
      {rows.map(([label, v, plus]) => (
        <div key={label} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
          <span style={sx('color:#3C454F;')}>{label}</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;')}>{(plus ? '+' : '') + M(v)}</span>
        </div>
      ))}
      <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:12px;padding-top:11px;border-top:1px solid rgba(34,39,46,.1);')}>
        <span style={sx('font-size:16px;font-weight:700;')}>Full-potential EV</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:20px;font-weight:700;color:#1F6B52;')}>{M(f.ev + upTotal)}</span>
      </div>
      <div style={sx('font-size:14px;color:#54606C;margin-top:5px;')}>{((f.ev + upTotal) / f.entry).toFixed(1) + 'x on entry at full execution · ask ' + M(f.entry)}</div>
    </div>
  );
}

function Risk({ p, f }: { p: TabProps; f: Deal }) {
  const { st, set } = p;
  const medCut = st.aiMedCut ?? 0;
  const occDrop = st.aiOccDrop ?? 0;
  const peer = peerOf(usePeerStaffing(p.client, [{ type: f.type, state: f.stt }]), f.type, f.stt);
  const medLoss = ((medCut / 100) * f.mcare * f.ppd * f.pd) / 1e6;
  const occLoss = ((occDrop / 100) * f.beds * 365 * f.ppd * 0.65) / 1e6;
  const sEbitda = f.ebitda - medLoss - occLoss;
  const sEV = f.ev * (sEbitda / f.ebitda);
  const sComp = f.comp - occDrop * 0.58;
  const sGrade = gradeOf(sComp);
  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:13px;')}>
      <div style={sx(CARD)}>
        <div style={sx(CAP + 'margin-bottom:14px;')}>Stress the deal</div>
        <div style={sx('display:flex;flex-direction:column;gap:15px;')}>
          <Slider label="Medicare rate cut" val={medCut + '%'} min={0} max={15} step={1} value={medCut} onChange={(e) => set({ aiMedCut: parseFloat(e.target.value) })} color="#B3261E" />
          <Slider label="Occupancy drop" val={occDrop + ' pts'} min={0} max={20} step={1} value={occDrop} onChange={(e) => set({ aiOccDrop: parseFloat(e.target.value) })} color="#B3261E" />
          {/* The design's "Simulate 3.48 HPRD mandate" toggle is not shipped: there is no federal
              staffing minimum (rescinded 2026-02-02). Staffing reads against same-type peers. */}
          <div data-testid="intel-risk-staffing">
            <span style={sx('display:inline-flex;align-items:center;gap:8px;height:30px;padding:0 13px;border-radius:8px;font-size:14px;white-space:nowrap;background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);')}>
              {'Staffing (HPRD) ' + staffingLine(f.hprd, peer)}
            </span>
            <div style={sx('font-size:14px;color:#54606C;margin-top:7px;')}>{peer?.median == null ? 'The typical figure for peers is not available for this group. No federal staffing minimum applies.' : 'Against ' + peer.cohort + ' peers. No federal staffing minimum applies.'}</div>
          </div>
        </div>
      </div>
      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
          <span style={sx(CAP)}>Under stress</span>
          <span style={sx('flex:1;')} />
          <BuildLabel><span style={sx(MOD_TAG)}>MODELED</span></BuildLabel>
        </div>
        <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px;')}>
          <div>
            <div style={sx(CAP9)}>Stressed EBITDA</div>
            <div style={sx('font-size:20px;font-weight:700;')}>{M(sEbitda)}</div>
            <div style={sx('font-size:14px;color:#B3261E;')}>{pc((sEbitda - f.ebitda) / f.ebitda, 1)}</div>
          </div>
          <div>
            <div style={sx(CAP9)}>Stressed EV</div>
            <div style={sx('font-size:20px;font-weight:700;')}>{M(sEV)}</div>
            <div style={sx('font-size:14px;color:#B3261E;')}>{pc((sEV - f.ev) / f.ev, 1)}</div>
          </div>
        </div>
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin-top:15px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
          <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:7px;font-size:14px;font-weight:700;color:#fff;background:' + GC[sGrade] + ';')}>{sGrade}</span>
          <span style={sx('font-size:14px;color:#54606C;')}>stressed grade</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:16px;font-weight:600;color:' + (sEV >= f.entry ? '#1F6B52' : '#B3261E') + ';')}>{sEV >= f.entry ? 'Still clears the ask' : 'Below ask under stress'}</span>
        </div>
      </div>
    </div>
  );
}

/** Live run and Ask Atlas: each button calls its governed route and echoes the answer. */
function LiveRun({ p, f }: { p: TabProps; f: Deal }) {
  const { st, set, client } = p;
  const cms = st.aiCms ?? f.providerId ?? '';
  const [ask, setAsk] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<string>) => {
    try {
      set({ aiRun: await fn() });
    } catch (e) {
      set({ aiRun: (e as Error).message });
    }
  };
  const runDcf = () =>
    run(async () => {
      const r = await intelDcf(client, { body: { initialCashFlow: f.ebitda * 0.681 * 1e6, growthRate: 0.055, discountRate: f.wacc, terminalGrowthRate: G, projectionYears: 5 } });
      return 'Run DCF · /dcf → EV ' + M(r.enterpriseValue / 1e6) + ' · ' + waccText(f) + ' · g 2.5%';
    });
  const runMc = () =>
    run(async () => {
      const r = await intelMonteCarlo(client, { body: { baseRevenue: f.rev * 1e6, target: 'ev', ev: f.ev * 1e6, askEv: f.entry * 1e6, simulations: 1600, bins: 23, years: 5 } });
      return 'Monte Carlo · /monte-carlo → ' + r.simulations.toLocaleString('en-US') + ' sims · P50 ' + M(r.percentiles.p50 / 1e6) + (r.pAboveAsk == null ? '' : ' · P(EV≥ask) ' + pc(r.pAboveAsk));
    });
  const lookup = () =>
    run(async () => {
      const r = await intelCmsLookup(client, { params: { providerId: cms } });
      const name = r.provider_name ?? r.name ?? cms;
      const beds = r.number_of_certified_beds ?? r.beds ?? DASH;
      const stars = r.overall_rating ?? DASH;
      return 'Lookup · /cms-lookup → ' + name + ' · ' + beds + ' beds · ' + stars + '★';
    });
  const score = () =>
    run(async () => {
      const r = await intelCmsScore(client, { params: { providerId: cms }, body: {} });
      const comp = r.composite ?? r.score?.composite;
      const grade = r.grade ?? r.score?.grade ?? DASH;
      return 'Score · /cms-score → composite ' + (comp == null ? DASH : Math.round(comp)) + '/100 · grade ' + grade;
    });
  const browse = () =>
    run(async () => {
      const r = await intelCmsFacilities(client, { query: { limit: 1 } });
      return 'Browse · /cms-facilities → ' + (r.total == null ? DASH : r.total.toLocaleString('en-US')) + ' rows · server-paged';
    });
  const askAtlas = async () => {
    const q = ask.trim();
    if (!q || busy) return;
    setBusy(true);
    try {
      const r = await intelAnalyst(client, { body: { question: 'About ' + f.name + ' (' + f.city + ', ' + f.stt + '): ' + q } });
      setAnswer(r.answer);
    } catch (e) {
      setAnswer((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const memo = async () => {
    if (!f.providerId) return;
    const res = await client.raw(intelSummaryPdfPath(f.providerId));
    if (!res.ok) {
      setAnswer(`The IC memo could not be made (${res.status}).`);
      return;
    }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:13px;')}>
      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
          <span style={sx(CAP)}>Live run</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>governed endpoints</span>
        </div>
        <div style={sx('font-size:14px;color:#54606C;margin-bottom:11px;')}>Valuation models on <span>{f.name}</span></div>
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
          <A style={BTN_RUN} onClick={runDcf}>
            Run DCF
          </A>
          <A style={BTN_RUN} onClick={runMc}>
            Monte Carlo
          </A>
        </div>
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:11px;flex-wrap:wrap;')}>
          <span style={sx(CAP9)}>CMS</span>
          <input
            type="text"
            aria-label="CMS provider number"
            value={cms}
            onChange={(e) => set({ aiCms: e.target.value })}
            style={sx('flex:1;min-width:120px;box-sizing:border-box;height:30px;padding:0 11px;border:1px solid rgba(34,39,46,.16);border-radius:7px;background:#FBF8F1;font-variant-numeric:tabular-nums;font-size:14px;color:#22272E;')}
          />
          <A style={BTN_SMALL} onClick={lookup}>
            Lookup
          </A>
          <A style={BTN_SMALL} onClick={score}>
            Score
          </A>
          <A style={BTN_SMALL} onClick={browse}>
            Browse
          </A>
        </div>
        <div data-testid="intel-run-echo" style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;margin-top:11px;padding-top:10px;border-top:1px solid rgba(34,39,46,.1);')}>
          {st.aiRun || 'No run this session.'}
        </div>
      </div>
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;display:flex;flex-direction:column;')}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
          <span style={sx(CAP)}>Ask Atlas</span>
          <A style="display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;text-decoration:none;background:#FBF8F1;color:#3C454F;border:1px solid rgba(34,39,46,.16);white-space:nowrap;" onClick={memo}>
            Full IC Memo
          </A>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>governed endpoint</span>
        </div>
        <div style={sx('flex:1;')}>
          {(busy || answer) && (
            <div role="status" style={sx('font-size:16px;color:#22272E;line-height:1.55;margin-bottom:11px;white-space:pre-wrap;text-wrap:pretty;')}>
              {busy ? 'Atlas is working on it…' : answer}
            </div>
          )}
        </div>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
          <input
            type="text"
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void askAtlas();
            }}
            placeholder={'Ask about ' + f.name + '…'}
            aria-label={'Ask Atlas about ' + f.name}
            style={sx('flex:1 1 130px;min-width:min(100%,130px);box-sizing:border-box;height:38px;padding:0 13px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;')}
          />
          <A style="display:inline-flex;align-items:center;height:38px;padding:0 17px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;background:#2F5C97;color:#fff;white-space:nowrap;" onClick={askAtlas}>
            Ask
          </A>
        </div>
      </div>
    </div>
  );
}
